/* =============================================================================
   HypoHero System Roadmap — combined page interactions
   - reveal-on-scroll via IntersectionObserver
   - spine "draw" fill tied to scroll progress through the journey
   - left rail active-section tracking + show/hide + background-tone detection
   - smooth-scroll for in-page anchors
   - before/after cell comparison slider
   - testimonial carousel
   - sticky bar / mobile bar / float button visibility
   - subscribe & save toggle (desktop + mobile)
   - dot chase animation on the hero SVG
   - respects prefers-reduced-motion and the no-motion tweak
   ============================================================================= */
(function () {
  'use strict';

  /* ---- Reduced motion detection ---- */
  var prefersReduced = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) document.body.classList.add('no-motion');

  /* ---- Reveal on scroll ---- */
  var io = null;
  if ('IntersectionObserver' in window && !document.body.classList.contains('no-motion')) {
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
  }
  // Observe any not-yet-revealed elements (optionally scoped to a root). When
  // there's no observer (no IO support / reduced motion), reveal immediately.
  function observeRevealElements(root) {
    var els = Array.prototype.slice.call(
      (root || document).querySelectorAll('.reveal:not(.in)'));
    if (io) {
      els.forEach(function (el) { io.observe(el); });
    } else {
      els.forEach(function (el) { el.classList.add('in'); });
    }
  }
  // Immediately reveal everything within a root (used in the theme editor).
  function revealAll(root) {
    Array.prototype.slice.call(
      (root || document).querySelectorAll('.reveal')).forEach(function (el) {
      el.classList.add('in');
    });
  }
  observeRevealElements();

  /* ---- Shopify Theme Editor: re-reveal on section/block re-render ----
     The editor swaps in fresh DOM after a setting change, so the original
     IntersectionObserver is no longer watching those nodes. Reveal them
     immediately (scroll animation is distracting in the editor) and re-observe
     in case the merchant previews the live page. */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      revealAll(event.target);
      observeRevealElements(event.target);
    });
    document.addEventListener('shopify:section:select', function (event) {
      revealAll(event.target);
    });
    document.addEventListener('shopify:block:select', function (event) {
      if (event.target.classList && event.target.classList.contains('reveal')) {
        event.target.classList.add('in');
      }
      revealAll(event.target);
    });
  }

  /* ---- Spine fill ---- */
  var journey = document.getElementById('journey');
  var fill = document.getElementById('spineFill');
  var ticking = false;

  function updateSpine() {
    if (!journey || !fill) return;
    // The journey div is a self-contained, absolutely-positioned overlay (not a
    // DOM parent of the phases). It is sized in DOCUMENT coordinates to span
    // from the top of the first phase to the bottom of the last phase, so it
    // scrolls naturally with the page. Using absolute (not fixed) avoids being
    // broken by any ancestor with a CSS transform, which would otherwise
    // re-anchor a fixed element and throw off the spine's position/centering.
    var phases = document.querySelectorAll('.phase');
    if (!phases.length) { journey.style.display = 'none'; return; }
    var pageY = window.pageYOffset || document.documentElement.scrollTop;
    var first = phases[0];
    var last = phases[phases.length - 1];
    var firstTop = first.getBoundingClientRect().top + pageY;
    var lastBottom = last.getBoundingClientRect().top + pageY + last.offsetHeight;
    var totalHeight = lastBottom - firstTop;
    if (totalHeight <= 0) { journey.style.display = 'none'; return; }
    // Position relative to the journey's offset parent (robust whether that is
    // the body or a positioned section wrapper).
    var parent = journey.offsetParent;
    var parentTop = parent ? parent.getBoundingClientRect().top + pageY : 0;
    journey.style.display = '';
    journey.style.top = (firstTop - parentTop) + 'px';
    journey.style.height = totalHeight + 'px';
    // Fill tracks to the middle of the viewport as it moves through the phases.
    var scrollMid = pageY + window.innerHeight * 0.5;
    var pct = Math.max(0, Math.min(1, (scrollMid - firstTop) / totalHeight));
    // The .spine is sized only via top:0/bottom:140px (no explicit height), so
    // both a child's percentage height AND reads of spine.offsetHeight can come
    // back 0 — which is why the line showed but never filled. Derive the spine
    // height directly from the journey height we set (minus the 140px bottom
    // inset), give the spine an explicit height, and set the fill in pixels.
    var spineHeight = totalHeight - 140;
    if (spineHeight < 0) spineHeight = 0;
    var spineEl = journey.querySelector('.spine');
    if (spineEl) spineEl.style.height = spineHeight + 'px';
    fill.style.height = (pct * spineHeight) + 'px';
  }

  /* ---- Rail active section + visibility + tone detection ---- */
  var rail = document.getElementById('rail');
  var railLinks = Array.prototype.slice.call(document.querySelectorAll('.rail a'));
  var sections = railLinks.map(function (a) {
    return document.getElementById(a.getAttribute('data-rail'));
  });
  var hero = document.getElementById('top');
  // Background-tone detection: sample the section sitting behind the rail's
  // vertical center, measure its luminance, and flip the rail to light /
  // medium / dark ink so ticks + labels keep contrast on every background.
  var bgSections = Array.prototype.slice.call(
    document.querySelectorAll('.s-violet, .s-lav, .s-offwhite'));

  function colorsFrom(el) {
    var cs = getComputedStyle(el), out = [];
    var bc = cs.backgroundColor;
    if (bc && bc !== 'transparent' && bc !== 'rgba(0, 0, 0, 0)') out.push(bc);
    var bi = cs.backgroundImage;
    if (bi && bi.indexOf('gradient') > -1) {
      var m = bi.match(/rgba?\([^)]+\)/g);
      if (m) out = out.concat(m);
    }
    return out;
  }
  function luminance(el) {
    var cols = colorsFrom(el);
    if (!cols.length) return null;
    var r = 0, g = 0, b = 0, n = 0;
    cols.forEach(function (c) {
      var p = c.match(/[\d.]+/g);
      if (!p) return;
      r += +p[0]; g += +p[1]; b += +p[2]; n++;
    });
    if (!n) return null;
    return (0.2126 * (r / n) + 0.7152 * (g / n) + 0.0722 * (b / n)) / 255;
  }

  function updateRail() {
    if (!rail) return;
    // sample background luminance behind the rail's vertical center
    var cy = window.innerHeight / 2;
    var lum = null;
    bgSections.forEach(function (sec) {
      var r = sec.getBoundingClientRect();
      if (r.top <= cy && r.bottom >= cy) {
        var l = luminance(sec);
        if (l != null) lum = l;
      }
    });
    if (lum == null) lum = luminance(document.body);
    if (lum == null) lum = 1;
    var tone = lum > 0.62 ? 'light' : (lum > 0.55 ? 'medium' : 'dark');
    rail.classList.remove('tone-light', 'tone-medium', 'tone-dark');
    rail.classList.add('tone-' + tone);
    // show rail only after scrolling past the hero
    if (hero) {
      var heroBottom = hero.getBoundingClientRect().bottom;
      rail.classList.toggle('show', heroBottom < window.innerHeight * 0.4);
    }
    var mid = window.innerHeight * 0.42;
    var activeIdx = -1;
    sections.forEach(function (sec, i) {
      if (!sec) return;
      var r = sec.getBoundingClientRect();
      if (r.top <= mid && r.bottom >= mid) activeIdx = i;
    });
    railLinks.forEach(function (a, i) {
      a.classList.toggle('active', i === activeIdx);
    });
  }

  /* ---- Scroll handler (spine + rail combined) ---- */
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      updateSpine();
      updateRail();
      ticking = false;
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  // initial paint
  updateSpine();
  updateRail();

  /* ---- Smooth scroll for anchor links ---- */
  document.querySelectorAll('a[href^="#"]').forEach(function (a) {
    a.addEventListener('click', function (ev) {
      var id = a.getAttribute('href').slice(1);
      if (!id) return;
      var target = document.getElementById(id);
      if (!target) return;
      ev.preventDefault();
      var y = window.pageYOffset + target.getBoundingClientRect().top - 8;
      window.scrollTo({ top: y, behavior: 'smooth' });
    });
  });

  /* ---- Before/after cell comparison slider ---- */
  (function () {
    var slider = document.getElementById('cellSlider');
    if (!slider) return;
    var dragging = false;
    function setPos(clientX) {
      var r = slider.getBoundingClientRect();
      var pct = ((clientX - r.left) / r.width) * 100;
      pct = Math.max(2, Math.min(98, pct));
      slider.style.setProperty('--pos', pct + '%');
    }
    function down(e) {
      dragging = true;
      setPos(e.touches ? e.touches[0].clientX : e.clientX);
      e.preventDefault();
    }
    function move(e) {
      if (!dragging) return;
      setPos(e.touches ? e.touches[0].clientX : e.clientX);
    }
    function up() { dragging = false; }
    slider.addEventListener('mousedown', down);
    slider.addEventListener('touchstart', down, { passive: false });
    window.addEventListener('mousemove', move);
    window.addEventListener('touchmove', move, { passive: true });
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up);
  })();

  /* ---- Testimonial carousel ---- */
  (function () {
    var quotes = document.querySelectorAll('.tquote');
    var dots = document.querySelectorAll('.tdot');
    var current = 0;
    var timer;
    function goTo(n) {
      quotes[current].classList.remove('active');
      dots[current].classList.remove('active');
      current = (n + quotes.length) % quotes.length;
      quotes[current].classList.add('active');
      dots[current].classList.add('active');
    }
    if (!quotes.length || !dots.length) return;
    dots.forEach(function (d) {
      d.addEventListener('click', function () {
        clearInterval(timer);
        goTo(parseInt(d.dataset.idx));
        timer = setInterval(function () { goTo(current + 1); }, 6000);
      });
    });
    timer = setInterval(function () { goTo(current + 1); }, 6000);
  })();

  /* ---- Sticky bar / mobile bar / float button visibility ---- */
  (function () {
    // Show sticky chrome only once the hero (id="top", first SR section) has
    // fully scrolled out of view. Detecting by the hero's position avoids any
    // dependency on the theme's site-header markup/selectors.
    var hero = document.getElementById('top');
    var stickyBar = document.getElementById('stickyBar');
    var stickyBtn = document.getElementById('stickyBtnFloat');
    var stickyMob = document.getElementById('stickyMobile');
    var heroCta = document.getElementById('heroCtaFloat');
    var buyBox = document.getElementById('start');

    function updateStickyVisibility() {
      if (!hero) return;
      var heroRect = hero.getBoundingClientRect();
      // Appear a bit sooner: trigger once the hero's bottom passes the site
      // header's height (so the header has scrolled off) rather than waiting
      // for the hero to fully clear the viewport. Falls back to 120px if the
      // theme header can't be measured.
      var headerOffset = 120;
      var hdr = document.querySelector(
        'header.header, .header-wrapper, .section-header, #shopify-section-header');
      if (hdr) {
        var h = hdr.getBoundingClientRect().height;
        if (h) headerOffset = h;
      }
      var pastHero = heroRect.bottom < headerOffset;  // hero mostly scrolled out
      // The sitewide header sits above the hero, so once the hero's top reaches
      // the viewport top, that header has scrolled off — safe to show the
      // pinned hero CTA without overlapping it.
      var headerGone = heroRect.top <= 0;
      var atBuyBox = buyBox
        ? buyBox.getBoundingClientRect().top < window.innerHeight * 0.5
        : false;
      var shouldShow = pastHero && !atBuyBox;

      if (stickyBar) {
        stickyBar.classList.toggle('visible', shouldShow);
        stickyBar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      }
      if (stickyMob) {
        stickyMob.classList.toggle('visible', shouldShow);
        stickyMob.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      }
      if (stickyBtn) {
        stickyBtn.classList.toggle('visible', shouldShow);
        // .in-bar gives the button its filled style so it reads on the white bar
        stickyBtn.classList.toggle('in-bar', shouldShow);
      }
      // Transparent hero CTA: pinned top-right while scrolling THROUGH the hero
      // (header gone, sticky bar not yet shown). Hides once the sticky bar takes
      // over, and stays hidden at the very top so it never covers the header.
      if (heroCta) {
        var showHeroCta = headerGone && !pastHero && !atBuyBox;
        heroCta.classList.toggle('visible', showHeroCta);
      }
    }

    window.addEventListener('scroll', updateStickyVisibility, { passive: true });
    window.addEventListener('resize', updateStickyVisibility, { passive: true });
    updateStickyVisibility();
  })();

  /* ---- Subscribe toggle (desktop + mobile) ---- */
  (function () {
    function wireToggle(toggleId) {
      var toggle = document.getElementById(toggleId);
      if (!toggle) return;
      var box = toggle.closest('.buybox--desktop, .buybox-mob');
      var price = box.querySelector('.buybox-price');
      var was = box.querySelector('.buybox-was');
      var save = box.querySelector('.buybox-save');
      var label = box.querySelector('.sub-label');

      toggle.addEventListener('change', function () {
        if (toggle.checked) {
          price.textContent = '$189';
          was.textContent = '$289';
          was.style.display = '';
          save.textContent = 'Save $100';
          save.style.display = '';
          label.textContent = 'Subscribe & Save · every 60 days';
        } else {
          price.textContent = '$289';
          was.style.display = 'none';
          save.style.display = 'none';
          label.textContent = 'One-Time Purchase';
        }
      });
    }
    wireToggle('subToggleDesktop');
    wireToggle('subToggleMobile');
  })();

  /* ---- Dot chase animation on hero SVG ---- */
  (function () {
    function run() {
      var svg = document.getElementById('heroSystemSvg');
      if (!svg) return;
      var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
      var cx = 540, cy = 540, dur = 3.6;
      var all = svg.querySelectorAll('circle, path, ellipse, rect');
      var spokes = [];
      all.forEach(function (el) {
        var b; try { b = el.getBBox(); } catch (e) { return; }
        var w = b.width, h = b.height; if (Math.max(w, h) >= 22) return;          // small dots only
        var ex = b.x + w / 2, ey = b.y + h / 2, r = Math.hypot(ex - cx, ey - cy);
        if (r >= 150 && r <= 300) { spokes.push({ el: el, r: r }); }               // thyroid -> icon spokes
        else { el.classList.add('dot-dim'); }                           // outer ring + stray ticks: dim back
      });
      if (reduce || !spokes.length) return;
      var rs = spokes.map(function (d) { return d.r; });
      var rMin = Math.min.apply(null, rs), rMax = Math.max.apply(null, rs), span = (rMax - rMin) || 1;
      spokes.forEach(function (d) {
        var rn = (d.r - rMin) / span;
        d.el.style.animationDelay = (-(1 - rn) * dur).toFixed(2) + 's';          // inner first -> wave travels outward
        d.el.classList.add('dot-chase');
      });
    }
    if (document.readyState !== 'loading') setTimeout(run, 60); else document.addEventListener('DOMContentLoaded', function () { setTimeout(run, 60); });
  })();

})();
